import type {
  ObjectPassportFieldDefinition,
  ObjectPassportFieldGroup,
  ObjectPassportFieldProps,
  ObjectPassportFieldTableViewOrder,
  ObjectPassportFieldValue,
  ObjectPassportFieldsPayload,
} from './types'

const EMPTY_PAYLOAD: ObjectPassportFieldsPayload = {
  fields: [],
  values: [],
  fieldGroups: [],
}

/**
 * Собирает URL ручки нормализованного паспорта объекта.
 * `:objectListId` — id элемента (`list_invent_objects.object_list_id`).
 * statusDate — срез версии (пусто = текущие значения).
 */
export function buildObjectPassportFieldsUrl(objectListId: number, statusDate?: string | null): string {
  const params = statusDate ? `?status_date=${encodeURIComponent(statusDate)}` : ''
  return `/objects/${objectListId}/passport${params}`
}

function toNumber(value: unknown): number | null {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function toNullableString(value: unknown): string | null {
  if (value === undefined || value === null) return null
  const s = String(value)
  return s === '' ? null : s
}

function toBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (value === 1 || value === '1' || value === 'true') return true
  if (value === 0 || value === '0' || value === 'false') return false
  return fallback
}

function toValidationProps(value: unknown): Record<string, unknown> {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return {}
}

function toTableViewOrder(value: unknown): ObjectPassportFieldTableViewOrder | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null
  const row = value as Record<string, unknown>
  return {
    row: toNumber(row.row),
    name: toNullableString(row.name),
    column: toNumber(row.column),
    colspan: toNumber(row.colspan),
    rowspan: toNumber(row.rowspan),
    view_type: row.view_type === 'value' || row.view_type === 'table' ? row.view_type : null,
    group_id: toNumber(row.group_id),
  }
}

function normalizeFieldPropsEntry(raw: unknown): ObjectPassportFieldProps | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const row = raw as Record<string, unknown>
  return {
    id: toNumber(row.id),
    table_view_order: toTableViewOrder(row.table_view_order),
    pseudo_sql: toNullableString(row.pseudo_sql),
    value_result: toNullableString(row.value_result),
    error: toNullableString(row.error),
  }
}

/**
 * API отдаёт `field_props` массивом размещений. Объект поддерживаем как совместимость
 * с ранними dev-ответами и нормализуем в массив из одной записи.
 */
function parseFieldProps(raw: unknown): ObjectPassportFieldProps[] {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeFieldPropsEntry)
      .filter((item): item is ObjectPassportFieldProps => item != null)
  }
  const entry = normalizeFieldPropsEntry(raw)
  return entry ? [entry] : []
}

/**
 * Парсит одно поле из `payloads.fields[]`.
 */
export function parseObjectPassportFieldDefinition(
  raw: unknown,
): ObjectPassportFieldDefinition | null {
  if (raw == null || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const id = toNumber(row.id)
  const catalogObjectsTypeId = toNumber(row.catalog_objects_type_id)
  const fieldType = toNumber(row.field_type)
  if (id == null || catalogObjectsTypeId == null || fieldType == null) return null

  const fieldProps = parseFieldProps(row.field_props)

  return {
    id,
    catalog_objects_type_id: catalogObjectsTypeId,
    field_type: fieldType,
    name: String(row.name ?? ''),
    catalog_id: toNumber(row.catalog_id),
    reference_id: toNumber(row.reference_id),
    mirror_field_id: toNumber(row.mirror_field_id),
    reverse_name: toNullableString(row.reverse_name),
    default_value: toNullableString(row.default_value),
    is_system: toBoolean(row.is_system),
    is_required: toBoolean(row.is_required),
    use_in_filter: toBoolean(row.use_in_filter),
    use_in_search: toBoolean(row.use_in_search),
    validation_props: toValidationProps(row.validation_props),
    sort: toNumber(row.sort) ?? 0,
    field_props: fieldProps,
  }
}

function parseValueList(raw: unknown): number[] | null {
  if (raw == null) return null
  if (!Array.isArray(raw)) return null
  const ids = raw.map((item) => toNumber(item)).filter((item): item is number => item != null)
  return ids.length > 0 ? ids : null
}

/**
 * Парсит одно значение из `data[]`.
 */
export function parseObjectPassportFieldValue(raw: unknown): ObjectPassportFieldValue | null {
  if (raw == null || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const fieldId = toNumber(row.field_id)
  if (fieldId == null) return null

  return {
    field_id: fieldId,
    value: toNullableString(row.value),
    value_list: parseValueList(row.value_list),
    value_result: toNullableString(row.value_result),
    error: toNullableString(row.error),
  }
}

/**
 * Парсит группу из `payloads.field_groups[]`.
 */
export function parseObjectPassportFieldGroup(raw: unknown): ObjectPassportFieldGroup | null {
  if (raw == null || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const id = toNumber(row.id)
  const catalogInventObjectsTypeId = toNumber(row.catalog_invent_objects_type_id)
  if (id == null || catalogInventObjectsTypeId == null) return null

  return {
    id,
    catalog_invent_objects_type_id: catalogInventObjectsTypeId,
    name: String(row.name ?? ''),
    open: toBoolean(row.open),
    position: String(row.position ?? ''),
    sort: toNumber(row.sort) ?? 0,
    is_tab: toBoolean(row.is_tab),
    type: String(row.type ?? ''),
    list_invent_object_type_id: toNumber(row.list_invent_object_type_id),
  }
}

/**
 * Парсит тело ответа GET /objects/:objectListId/passport.
 * Некорректный payload не роняет UI — возвращает пустые массивы.
 */
export function parseObjectPassportFieldsResponse(raw: unknown): ObjectPassportFieldsPayload {
  if (raw == null || typeof raw !== 'object') return { ...EMPTY_PAYLOAD }

  const root = raw as Record<string, unknown>
  const payloads =
    root.payloads != null && typeof root.payloads === 'object'
      ? (root.payloads as Record<string, unknown>)
      : {}

  const fields = (Array.isArray(payloads.fields) ? payloads.fields : [])
    .map(parseObjectPassportFieldDefinition)
    .filter((item): item is ObjectPassportFieldDefinition => item != null)

  const values = (Array.isArray(root.data) ? root.data : [])
    .map(parseObjectPassportFieldValue)
    .filter((item): item is ObjectPassportFieldValue => item != null)

  const fieldGroups = (Array.isArray(payloads.field_groups) ? payloads.field_groups : [])
    .map(parseObjectPassportFieldGroup)
    .filter((item): item is ObjectPassportFieldGroup => item != null)

  return { fields, values, fieldGroups }
}
